import {
  BREAKPOINT_MOBILE,
  BREAKPOINT_TABLET,
  useMediaQuery,
} from "@gotts.ai/ui";
import { useCallback, useMemo, useState } from "react";

const LS_KEY = "devenv-sidebar-collapsed";

function readPref(): boolean | null {
  try {
    const v = localStorage.getItem(LS_KEY);
    return v === null ? null : v === "true";
  } catch {
    return null;
  }
}

function writePref(collapsed: boolean): void {
  try {
    localStorage.setItem(LS_KEY, String(collapsed));
  } catch {
    // ignore
  }
}

export interface SidebarState {
  collapsed: boolean;
  isMobile: boolean;
  isDesktop: boolean;
  showDrawer: boolean;
  toggle: () => void;
  closeDrawer: () => void;
}

export function useSidebarState(): SidebarState {
  const isMobile = useMediaQuery(
    `(max-width: ${String(BREAKPOINT_MOBILE - 1)}px)`,
  );
  const isDesktop = useMediaQuery(
    `(min-width: ${String(BREAKPOINT_TABLET)}px)`,
  );

  const [manualCollapsed, setManualCollapsed] = useState<boolean | null>(
    readPref,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  const collapsed = useMemo(() => {
    if (isMobile) return true;
    if (manualCollapsed !== null) return manualCollapsed;
    return !isDesktop; // tablet defaults to collapsed
  }, [isMobile, isDesktop, manualCollapsed]);

  const showDrawer = isMobile && drawerOpen;

  const toggle = useCallback(() => {
    if (isMobile) {
      setDrawerOpen((prev) => !prev);
    } else {
      setManualCollapsed((prev) => {
        const next = prev === null ? !(!isDesktop ? true : false) : !prev;
        writePref(next);
        return next;
      });
    }
  }, [isMobile, isDesktop]);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  return { collapsed, isMobile, isDesktop, showDrawer, toggle, closeDrawer };
}
